import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

/**
 * PublicRoute Component
 * 
 * Protects public routes (like login/register) from authenticated users
 * Redirects authenticated users to their dashboard
 * Allows unauthenticated users to access the route
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // If user is authenticated, redirect to dashboard
  if (isAuthenticated) {
    // Redirect target, in order: ?next= on the URL (app-relative only), a
    // next kept across the verify-email step (guest project wizard), the
    // router state, else the dashboard. This runs before the login page's
    // own effect, so it has to know about all of them.
    const safe = (v) => (v && v.startsWith('/') && !v.startsWith('//') ? v : null);
    const nextParam = safe(new URLSearchParams(location.search || '').get('next'));
    let storedNext = null;
    try { storedNext = safe(localStorage.getItem('pending_verification_next')); if (storedNext) localStorage.removeItem('pending_verification_next'); } catch (_) { /* ignore */ }
    const fromState = location.state?.from;
    const from = nextParam || storedNext || (fromState
      ? `${fromState.pathname || '/dashboard'}${fromState.search || ''}${fromState.hash || ''}`
      : '/dashboard');
    return <Navigate to={from} replace />;
  }

  // User is not authenticated, show the public route
  return children;
};

export default PublicRoute;
